import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { companyAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import BackButton from '../components/BackButton';
import { PageError, loadError, OFFLINE } from '../components/OfflineNotice';

function UpgradeWall() {
  const navigate = useNavigate();
  return (
    <div className="page">
      <BackButton />
      <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🏢</div>
        <h2 style={{ fontWeight: 700, marginBottom: '0.5rem' }}>Enterprise Feature</h2>
        <p style={{ color: 'var(--color-muted)', marginBottom: '1.5rem', maxWidth: '320px', margin: '0 auto 1.5rem' }}>
          Multi-business management, fund tracking, and consolidated financials are available on the Enterprise plan.
        </p>
        <button
          onClick={() => navigate('/upgrade')}
          style={{ background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color:'#fff', border:'none', borderRadius:'12px', padding:'1.25rem 2rem', fontWeight:700, fontSize:'1rem', cursor:'pointer', marginTop:'0.5rem' }}
        >
          View Enterprise plan →
        </button>
      </div>
    </div>
  );
}

export default function CompanyDashboard() {
  const { parentCompany, isEnterprise } = useAuth();
  const navigate = useNavigate();
  const [financials, setFinancials] = useState(null);
  const [fund, setFund]             = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([companyAPI.financials(), companyAPI.fundBalance()])
      .then(([fRes, bRes]) => {
        setFinancials(fRes.data);
        setFund(bRes.data.fund);
      })
      .catch((err) => setError(loadError(err, 'Failed to load company data')))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // Back online after the page failed for lack of a connection: load again.
  useEffect(() => {
    if (error !== OFFLINE) return undefined;
    const onOnline = () => load();
    window.addEventListener('online', onOnline);
    return () => window.removeEventListener('online', onOnline);
  }, [error]);

  const fmt = (n) =>
    new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(Number(n) || 0);

  const cur = parentCompany?.currency || '';

  if (!isEnterprise) return <UpgradeWall />;
  if (loading) return <div className="page"><p className="text-muted">Loading…</p></div>;
  if (error)   return <div className="page"><h1 className="page-title">{parentCompany?.name}</h1><PageError error={error} /></div>;

  const { businesses = [], totals = {} } = financials || {};
  const balance = Number(fund?.balance) || 0;

  const KPI_DEFS = [
    { key: 'revenue',      label: 'Total Revenue', value: totals.revenue,      color: null },
    { key: 'gross_profit', label: 'Gross Profit',  value: totals.gross_profit, color: (v) => v >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
    { key: 'net_profit',   label: 'Net Profit',    value: totals.net_profit,   color: (v) => v >= 0 ? 'var(--color-success)' : 'var(--color-danger)' },
    { key: 'receivables',  label: 'Receivables',   value: totals.receivables,  color: () => 'var(--color-warning)' },
  ];

  return (
    <div className="page">
      <h1 className="page-title">{parentCompany?.name}</h1>
      <p className="text-muted" style={{ marginBottom: '1rem' }}>{cur} · Company overview</p>

      {/* Fund balance hero */}
      <Link to="/company/fund" style={{ textDecoration: 'none' }}>
        <div className="card card-3d" style={{ marginBottom: '1rem', textAlign: 'center', padding: '1.5rem 1rem', cursor: 'pointer' }}>
          <div style={{ fontSize: '0.8rem', color: 'var(--color-muted)', marginBottom: '0.25rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Available Funds</div>
          <div style={{ fontSize: '2rem', fontWeight: 700, color: balance >= 0 ? 'var(--color-success)' : 'var(--color-danger)' }}>
            {cur} {fmt(balance)}
          </div>
        </div>
      </Link>

      {/* Consolidated KPIs */}
      <div className="kpi-grid" style={{ marginBottom: '1rem' }}>
        {KPI_DEFS.map(({ key, label, value, color }) => {
          const val = Number(value) || 0;
          return (
            <div
              key={key}
              className="card kpi-card"
              onClick={() => navigate(`/company/kpi/${key}`)}
              style={{ cursor: 'pointer' }}
            >
              <div className="kpi-label">{label}</div>
              <div className="kpi-value" style={{ color: color ? color(val) : 'inherit' }}>{cur} {fmt(val)}</div>
            </div>
          );
        })}
      </div>

      {/* Per-business list */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600, margin: 0 }}>Businesses</h2>
        <button
          className="qa-btn"
          style={{ flex: 'none', padding: '0.45rem 0.9rem', fontSize: '0.85rem' }}
          onClick={() => navigate('/company/add-business')}
        >
          + Add Business
        </button>
      </div>

      {businesses.length === 0 && (
        <p className="text-muted" style={{ fontSize: '0.9rem' }}>No businesses yet.</p>
      )}
      {businesses.map(b => {
        const revenue     = Number(b.revenue);
        const grossProfit = revenue - Number(b.cogs);
        const netProfit   = grossProfit - Number(b.expenses);
        return (
          <div key={b.id} className="card card-3d" onClick={() => navigate(`/company/business/${b.id}`)} style={{ marginBottom: '0.65rem', cursor: 'pointer' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <span style={{ fontWeight: 600, fontSize: '0.95rem' }}>{b.name}</span>
              <span style={{
                fontSize: '0.72rem', fontWeight: 600, padding: '0.2rem 0.55rem', borderRadius: '999px',
                background: netProfit < 0 ? 'rgba(239,68,68,0.15)' : 'rgba(34,197,94,0.15)',
                color: netProfit < 0 ? 'var(--color-danger)' : 'var(--color-success)',
              }}>
                {netProfit < 0 ? 'Loss' : 'Profit'}
              </span>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {[
                ['Revenue',     b.revenue,    null],
                ['Gross Profit', grossProfit, grossProfit >= 0 ? 'var(--color-success)' : 'var(--color-danger)'],
                ['Net Profit',   netProfit,   netProfit   >= 0 ? 'var(--color-success)' : 'var(--color-danger)'],
              ].map(([label, val, color]) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.83rem', color: 'var(--color-muted)' }}>{label}</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 500, color: color || 'inherit' }}>
                    {cur} {fmt(val)}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Quick actions */}
      <div className="quick-actions" style={{ marginTop: '1.5rem' }}>
        <Link to="/company/fund"        className="qa-btn">Fund</Link>
        <Link to="/expenses"            className="qa-btn">Expenses</Link>
        <Link to="/company/financials"  className="qa-btn">Statements & Export</Link>
      </div>
    </div>
  );
}
